import { AlertCircle, FileText, Loader2, RotateCcw, Upload, X } from "lucide-react"

import { Button } from "@/components/ui/button"

// The new flow's one-file upload control (the supplied outline, the editing
// manuscript): empty, uploading, uploaded, failed with a retry, unsupported,
// too large.
// The file itself saves through the page's draft save queue; this only shows
// where that save is and hands a chosen file, a retry or a removal back.
//
// saved: the saved file's name, or null. upload: { status: "idle" |
// "uploading" | "done" | "failed" | "unsupported" | "tooLarge", file }. words:
// the service's own strings ({ uploading, uploaded, failed, retry, empty,
// button, unsupported, tooLarge }); `empty` and `tooLarge` may be omitted.

export function ErrorLine({ children }) {
  if (!children) return null
  return (
    <p role="alert" className="flex items-center gap-1.5 text-[12px] font-semibold text-destructive">
      <AlertCircle className="size-3.5 shrink-0" strokeWidth={2.2} />
      {children}
    </p>
  )
}

function FileRow({ name, note, children }) {
  return (
    <div className="flex items-center gap-3 rounded-[10px] border border-border bg-card px-3.5 py-2.5">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-secondary">
        <FileText className="size-4 text-muted-foreground" strokeWidth={1.9} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-semibold text-foreground">{name}</span>
        {note && <span className="block text-[11.5px] text-muted-foreground">{note}</span>}
      </span>
      {children}
    </div>
  )
}

export function UploadControl({ saved, upload, accept, words, onFile, onRetry, onRemove }) {
  const status = upload?.status || "idle"

  if (status === "uploading") {
    return (
      <FileRow name={upload.file?.name} note={words.uploading}>
        <Loader2 className="size-4 animate-spin text-muted-foreground" strokeWidth={2} />
      </FileRow>
    )
  }

  if (status === "failed") {
    return (
      <>
        <FileRow name={upload.file?.name}>
          <Button variant="outline" size="sm" onClick={onRetry} className="gap-1.5">
            <RotateCcw className="size-3.5" strokeWidth={2} />
            {words.retry}
          </Button>
        </FileRow>
        <ErrorLine>{words.failed}</ErrorLine>
      </>
    )
  }

  if (saved) {
    return (
      <FileRow name={saved} note={status === "done" ? words.uploaded : null}>
        <button
          type="button"
          aria-label="Remove file"
          onClick={onRemove}
          className="flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <X className="size-3.5" strokeWidth={2} />
        </button>
      </FileRow>
    )
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 rounded-[11px] border border-dashed border-line-2 px-4 py-3.5">
        {words.empty && <span className="min-w-0 flex-1 text-[12.5px] text-muted-foreground">{words.empty}</span>}
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-accent">
          <Upload className="size-3.5" strokeWidth={2} />
          {words.button}
          <input
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files && e.target.files[0]
              e.target.value = ""
              if (file) onFile(file)
            }}
          />
        </label>
      </div>
      {status === "unsupported" && <ErrorLine>{words.unsupported}</ErrorLine>}
      {status === "tooLarge" && <ErrorLine>{words.tooLarge}</ErrorLine>}
    </>
  )
}
